"use client";

import { useEffect, useMemo, useState } from "react";
import { X, Search, Download, ArrowUpDown, ArrowUp, ArrowDown, Table2 } from "lucide-react";
import { formatValue, statusColor, alignFor, semanticColor } from "./formatters";
import type { RichTable, RichColumnFormat } from "@/lib/caonabo/rich-content";

type SortDir = "asc" | "desc" | null;

type Props = {
  data: RichTable;
  open: boolean;
  onClose: () => void;
};

const TERRA_LIGHT = "#D77F70";

export default function ChatTableModal({ data, open, onClose }: Props) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter((row) =>
      data.columns.some((col) => {
        const v = row[col.key];
        return v != null && String(v).toLowerCase().includes(q);
      })
    );
  }, [data.rows, data.columns, search]);

  const sortedRows = useMemo(() => {
    if (!sortKey || !sortDir) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const va = a[sortKey] ?? "";
      const vb = b[sortKey] ?? "";
      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      return sortDir === "asc"
        ? String(va).toLowerCase().localeCompare(String(vb).toLowerCase())
        : String(vb).toLowerCase().localeCompare(String(va).toLowerCase());
    });
  }, [filteredRows, sortKey, sortDir]);

  function handleSort(key: string) {
    if (sortKey === key) {
      if (sortDir === "asc") setSortDir("desc");
      else if (sortDir === "desc") {
        setSortKey(null);
        setSortDir(null);
      }
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function exportCSV() {
    const header = data.columns.map((c) => `"${c.label}"`).join(",");
    const body = sortedRows
      .map((row) =>
        data.columns
          .map((c) => `"${String(row[c.key] ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");
    const blob = new Blob([`${header}\n${body}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.title.replace(/[^a-z0-9]/gi, "_").toLowerCase()}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm caonabo-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8 pointer-events-none"
        role="dialog"
        aria-modal="true"
        aria-label={data.title}
      >
        <div
          className="caonabo-slide-up relative w-full max-w-6xl h-full sm:h-[90vh] bg-surface rounded-2xl overflow-hidden flex flex-col pointer-events-auto border"
          style={{
            borderColor: "rgba(139,58,46,0.30)",
            boxShadow: "0 25px 60px rgba(0,0,0,0.55), 0 6px 16px rgba(139,58,46,0.30)",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 sm:px-6 py-3 flex-shrink-0 border-b"
            style={{
              borderBottomColor: "rgba(139,58,46,0.25)",
              background:
                "linear-gradient(135deg, rgba(139,58,46,0.14) 0%, rgba(139,58,46,0.04) 100%)",
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "rgba(139,58,46,0.20)" }}
              >
                <Table2 className="h-5 w-5" style={{ color: TERRA_LIGHT }} />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-text truncate">
                  {data.title}
                </h3>
                <p className="text-[11px] text-text-muted truncate">
                  {sortedRows.length} {sortedRows.length === 1 ? "fila" : "filas"}
                  {search && ` de ${data.rows.length}`}
                  {data.subtitle ? ` · ${data.subtitle}` : ""}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={exportCSV}
                className="px-3 py-1.5 rounded-xl text-[11px] font-medium transition-colors flex items-center gap-1.5"
                style={{
                  background: "rgba(139,58,46,0.18)",
                  color: TERRA_LIGHT,
                  border: "1px solid rgba(139,58,46,0.30)",
                }}
              >
                <Download className="h-3.5 w-3.5" />
                CSV
              </button>
              <button
                onClick={onClose}
                aria-label="Cerrar"
                className="p-2 rounded-xl hover:bg-white/5 transition-colors text-text-muted hover:text-text"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div
            className="px-4 sm:px-6 py-2.5 border-b flex-shrink-0"
            style={{
              borderBottomColor: "rgba(139,58,46,0.15)",
              background: "rgba(0,0,0,0.10)",
            }}
          >
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border focus-within:border-[#D77F70] transition-colors"
              style={{
                background: "rgba(255,255,255,0.04)",
                borderColor: "rgba(139,58,46,0.20)",
              }}
            >
              <Search className="h-3.5 w-3.5 text-text-muted flex-shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar en la tabla..."
                autoFocus
                className="flex-1 bg-transparent text-sm focus:outline-none placeholder-text-muted text-text"
              />
              {search && (
                <span className="text-[11px] text-text-muted tabular-nums">
                  {sortedRows.length}/{data.rows.length}
                </span>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-auto">
            <table className="w-full text-[13px]">
              <thead
                className="sticky top-0 z-10"
                style={{ background: "rgba(20,20,28,0.95)" }}
              >
                <tr style={{ borderBottom: "1px solid rgba(139,58,46,0.20)" }}>
                  {data.columns.map((col) => {
                    const align = alignFor(col.format);
                    const isActive = sortKey === col.key;
                    return (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className="px-4 py-2 font-semibold text-text-muted whitespace-nowrap cursor-pointer select-none transition-colors group"
                        style={{ textAlign: align }}
                      >
                        <span
                          className="inline-flex items-center gap-1.5"
                          style={{
                            justifyContent: align === "right" ? "flex-end" : "flex-start",
                          }}
                        >
                          {col.label}
                          {isActive ? (
                            sortDir === "asc" ? (
                              <ArrowUp className="h-3 w-3" style={{ color: TERRA_LIGHT }} />
                            ) : (
                              <ArrowDown className="h-3 w-3" style={{ color: TERRA_LIGHT }} />
                            )
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={data.columns.length}
                      className="text-center py-12 text-text-muted text-sm"
                    >
                      {search ? "Sin resultados para esta búsqueda" : "Sin datos"}
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((row, i) => (
                    <tr
                      key={i}
                      className="hover:bg-white/5 transition-colors"
                      style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                    >
                      {data.columns.map((col) => (
                        <ModalCell key={col.key} col={col} value={row[col.key]} />
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function ModalCell({
  col,
  value,
}: {
  col: { key: string; label: string; format?: RichColumnFormat };
  value: unknown;
}) {
  const align = alignFor(col.format);
  if (col.format === "status" && value != null) {
    const s = statusColor(String(value));
    return (
      <td className="px-4 py-2">
        <span
          className="text-[11px] font-bold px-2 py-0.5 rounded"
          style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}
        >
          {formatValue(value, "status")}
        </span>
      </td>
    );
  }
  const semStyle = semanticColor(col.key, value);
  return (
    <td
      className="px-4 py-2 whitespace-nowrap text-text"
      style={{
        textAlign: align,
        fontVariantNumeric: align === "right" ? "tabular-nums" : undefined,
        ...(semStyle
          ? Object.fromEntries(
              semStyle.split(";").filter(Boolean).map((p) => {
                const [k, v] = p.split(":").map((x) => x.trim());
                const camel = k.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                return [camel, v];
              })
            )
          : {}),
      }}
    >
      {formatValue(value, col.format)}
    </td>
  );
}
